import noop from 'lodash/noop';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { H2, HStack, Heading, Label, P, VStack } from '@eluve/components';
import {
  ClassicSummary,
  DynamicSummary,
  dynamicArtifactTemplateSchema,
} from '@eluve/llm-outputs';
import {
  DynamicArtifactRenderer,
  SummarySectionMarkdown,
} from '@eluve/smart-blocks';
import { Translator } from '@eluve/translation';
import {
  AppointmentSummaryKeys,
  summaryKeyToSectionTitleMap,
} from '@eluve/utils';
import {
  BillingCode,
  getVendorDataMapper,
  isClassicSummary,
  isDynamicSummary,
} from '@eluve/vendor';

import { ehrDiffContentFragment } from './operations';

export const EhrDraftContent: React.FC = () => {
  const { appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const appointment = useCompleteFragment({
    fragment: ehrDiffContentFragment,
    key: { id: appointmentId },
  });

  const humanSoapNote = appointment?.humanOutputs?.[0];
  const outputVariant =
    humanSoapNote?.output?.llm_output?.metadata?.output_variant;

  let ehrSignedNote: ClassicSummary | DynamicSummary | undefined;
  let signedBillingCodes: BillingCode[] | undefined;

  const externalChart = appointment?.external_charts?.[0];
  const externalEhrVendor = externalChart?.external_ehr?.vendor;

  if (externalEhrVendor) {
    const vendorProvider = getVendorDataMapper(externalEhrVendor);
    const artifactTemplate = outputVariant
      ? dynamicArtifactTemplateSchema.parse(outputVariant.template)
      : undefined;

    if (vendorProvider.convertChartToSummary && artifactTemplate) {
      const convertedSummary = vendorProvider.convertChartToSummary({
        data: externalChart?.rawData ?? {},
        template: artifactTemplate,
      });

      if (convertedSummary) {
        ehrSignedNote = convertedSummary.summary;
        signedBillingCodes = convertedSummary.billingCodes;
      }
    }
  }

  const [translatedContent, setTranslatedContent] = useState<string | null>(
    null,
  );

  // Content for translation - only EHR content
  const toTranslate = {
    ehrSignedNote,
    signedBillingCodes,
  };

  const parsedTranslatedContent: typeof toTranslate = translatedContent
    ? JSON.parse(translatedContent)
    : null;

  const translatedEhrSignedNote = parsedTranslatedContent?.ehrSignedNote;
  const translatedSignedBillingCodes =
    parsedTranslatedContent?.signedBillingCodes;

  const displayEhrSignedNote = translatedEhrSignedNote ?? ehrSignedNote;
  const displaySignedBillingCodes =
    translatedSignedBillingCodes ?? signedBillingCodes;

  return (
    <VStack>
      <HStack className="mt-4" justify="between" align="center">
        <Heading>EHR Draft</Heading>
      </HStack>

      <div className="mb-4 w-1/3">
        <Label>Need translation?</Label>
        <Translator
          originalContent={JSON.stringify(toTranslate)}
          setTranslatedContent={setTranslatedContent}
        />
      </div>

      {displayEhrSignedNote && isDynamicSummary(displayEhrSignedNote) && (
        <DynamicArtifactRenderer
          isReadonly={true}
          blocks={displayEhrSignedNote.blocks}
        />
      )}

      {displayEhrSignedNote && isClassicSummary(displayEhrSignedNote) && (
        <VStack gap={8}>
          {Object.entries(summaryKeyToSectionTitleMap).map(
            ([key, sectionTitle]) => {
              const summaryKey = key as AppointmentSummaryKeys;
              const value = displayEhrSignedNote?.data?.[summaryKey] ?? '';
              return (
                <VStack key={summaryKey} align="stretch" gap={4}>
                  <H2>{sectionTitle}</H2>
                  <SummarySectionMarkdown
                    summaryKey={summaryKey}
                    sectionTitle=""
                    content={
                      typeof value === 'string'
                        ? value
                        : JSON.stringify(value, null, 2)
                    }
                    disabled={true}
                    handleContentChange={noop}
                  />
                </VStack>
              );
            },
          )}
        </VStack>
      )}

      <HStack>
        <Heading>Billing Codes</Heading>
      </HStack>
      {Boolean(displaySignedBillingCodes?.length) && (
        <VStack gap={2}>
          {displaySignedBillingCodes!.map(
            (billingCode: BillingCode, index: number) => (
              <P key={index}>
                {billingCode.code} - {billingCode.description}
              </P>
            ),
          )}
        </VStack>
      )}
    </VStack>
  );
};
